/*
 * Copyright 2025 SJTU-Geek
 * Copyright 2020-2025 SJTU-Plus
 *
 * ----------------------------------------------------------------
 * Modified by Niazye and ChiyoYuki from ECNU-minus on 2025-12-02
 * Changes:
 * - feat: hide classtable page
 *
 * Modified by 霧雨バニラ from BetterECNU on 2026-05-23
 * Changes:
 * - feat: 添加全局搜索导航链接，独立页面通过 SWR 获取默认学期，支持 hideToggle 属性隐藏侧栏切换按钮
 *
 * Copyright 2025 ECNU-minus
 * ----------------------------------------------------------------
 */

import React from 'react'
import { Link, matchPath, useLocation, useParams } from 'react-router-dom'
import useSWR from 'swr'

import fetcher from './SWRFetcher'

function CoursePlusSvg() {
  return (
    <svg
      width='1.25rem'
      height='1.25rem'
      viewBox='0 0 16 16'
      className='mr-1 align-middle bi bi-calendar-plus'
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
    >
      <defs>
        <linearGradient id='gradient' gradientTransform='rotate(45)'>
          <stop offset='0%' stopColor='#24C6DC' />
          <stop offset='100%' stopColor='#514A9D' />
        </linearGradient>
      </defs>
      <path
        fillRule='evenodd'
        fill='url(#gradient)'
        d='M4 .5a.5.5 0 0 0-1 0V1H2a2 2 0 0 0-2 2v1h16V3a2 2 0 0 0-2-2h-1V.5a.5.5 0 0 0-1 0V1H4V.5zM16 14V5H0v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2zM8.5 8.5a.5.5 0 0 0-1 0V10H6a.5.5 0 0 0 0 1h1.5v1.5a.5.5 0 0 0 1 0V11H10a.5.5 0 0 0 0-1H8.5V8.5z'
      />
    </svg>
  )
}

export default ({
  onToggleClick,
  hideToggle,
}: {
  onToggleClick: () => void
  hideToggle: boolean
}) => {
  const { pathname } = useLocation()
  const params = useParams<{ semester?: string }>()
  const isStandalone = pathname === '/search' || pathname === '/course'
  const semesterFromPath = pathname.match(/^\/([^/]+)/)?.[1]

  const { data: indexData } = useSWR(
    isStandalone ? '/course-plus-data/lessonData_index.json' : null,
    fetcher
  )
  const defaultSemester =
    indexData && indexData.length > 0
      ? `${indexData[indexData.length - 1].year}_${
          indexData[indexData.length - 1].semester
        }`
      : null

  const semester = isStandalone
    ? defaultSemester || null
    : params.semester || semesterFromPath
  const links = [
    { path: '/browse', text: '搜索', match: '/:semester/browse' },
    { path: '/plan', text: '排课', match: '/:semester/plan' },
    // { path: '/classtable', text: '课表', match: '/:semester/classtable' },
  ]
  const match_links = links.map((link) => ({
    path: link.path,
    text: link.text,
    match: matchPath(pathname, {
      path: link.match,
    }),
  }))
  const linksComponent = match_links.map((link) => (
    <li key={link.path} className={`nav-item${link.match ? ' active' : ''}`}>
      <Link to={`/${semester}${link.path}`} className='nav-link'>
        {link.text}
      </Link>
    </li>
  ))

  // Global search link - independent of semester
  const globalSearchMatch =
    matchPath(pathname, { path: '/search' }) ||
    matchPath(pathname, { path: '/course' })
  const globalSearchLink = (
    <li
      key='/search'
      className={`nav-item${globalSearchMatch ? ' active' : ''}`}
    >
      <Link to='/search' className='nav-link'>
        全局搜索
      </Link>
    </li>
  )

  return (
    <nav className='mb-3 navbar navbar-light navbar-expand-md course-navbar'>
      {!hideToggle && (
        <button
          className='navbar-toggler d-md-none'
          type='button'
          onClick={onToggleClick}
        >
          <span className='navbar-toggler-icon'></span>
        </button>
      )}
      <span
        className='mx-2 navbar-text d-md-none'
        style={{
          color: '#ffc107',
          backgroundColor: '#f2f2f2',
          padding: '0.1rem 0.35rem',
          borderRadius: '0.25rem',
        }}
      >
        为保证最佳体验，建议使用PC端访问
      </span>
      <span className='mb-0 navbar-brand'>
        <CoursePlusSvg /> <span className='align-middle'>Course+</span>
      </span>
      <div className='navbar-collapse show'>
        <ul className='ml-auto navbar-nav'>
          {semester ? linksComponent : null}
          {globalSearchLink}
        </ul>
      </div>
    </nav>
  )
}
